import {
  useContext,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

import {
  Chip,
  FormControl,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { toast } from "@rubi/ui";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { matchSorter } from "match-sorter";
import { Trans, useTranslation } from "react-i18next";

import {
  useAlertCrimeTypesQuery,
  useBulletinTypesQuery,
} from "@/api/loadAlerts";
import { colors } from "@/common/colors";
import "@/components/alerts/alerts.scss";
import {
  ALERT_CLASSIFICATION,
  generateValidationCriteria,
} from "@/components/alerts/constants";
import {
  AlertViewContext,
  SingleAlertContext,
} from "@/components/alerts/context";
import { IPoliceService } from "@/types";

import AddressList from "./AddressList";
import { AdvancedDetails } from "./AdvancedDetails";
import { ContactInfoBox } from "./ContactInfoBox";
import IncidentTimeSelector from "./DisplayAlert/IncidentTimeSelector";

const filterOccuranceNumbers = createFilterOptions<{
  inputValue: string;
  label: string;
}>();

export const filterPoliceServiceOptions = (options, { inputValue }) =>
  matchSorter(options, inputValue, { keys: ["name"] });

interface IDetailsStepProps {
  validationAttempted: boolean;
  onPoliceServiceSelect: (policeService: IPoliceService) => void;
  setType: (type: string) => void;
  updateState: (key: string, value: any) => void;
  setDescription: (description: string) => void;
  setIncidentTimes: (
    incidentTimes: {
      time_type: string;
      start_date: any | null;
      end_date: any | null;
    }[],
  ) => void;
  type: string;
  associatedCrimes: any;
  occurrenceNumbers: any;
  incidentTimes: {
    time_type: string;
    start_date: any | null;
    end_date: any | null;
  }[];
  description: string;
  policeService: IPoliceService;
  setErrors: (errors: any) => void;
}

export interface DetailsStepHandle {
  scrollToFirstError: () => void;
}

export const DetailsStep = forwardRef<DetailsStepHandle, IDetailsStepProps>(
  ({ ...props }, ref) => {
    const { alertProperties, setAlertProperty, errors, setErrors } =
      useContext(SingleAlertContext);
    const { policeService, contacts, rawAddresses } = alertProperties;

    const { policeServices } = useContext(AlertViewContext);
    const matchedService = policeServices.find(
      (ps) => ps.id === policeService?.id,
    );
    const operationalAreas =
      matchedService?.rubi_org?.operational_areas ??
      policeService?.rubi_org?.operational_areas;
    const { data: crimeTypes = [] } = useAlertCrimeTypesQuery();
    const { data: bulletinTypes = [] } = useBulletinTypesQuery();
    const { t } = useTranslation([
      "bulletin/create",
      "bulletin/associatedCrime",
    ]);
    const [addresses, setAddresses] = useState<any>(
      alertProperties.rawAddresses,
    );

    // Add refs for each form section that can have errors
    const policeServiceRef = useRef<HTMLDivElement>(null);
    const typeRef = useRef<HTMLDivElement>(null);
    const associatedCrimesRef = useRef<HTMLDivElement>(null);
    const operationalAreaRef = useRef<HTMLDivElement>(null);
    const occurrenceNumbersRef = useRef<HTMLDivElement>(null);
    const incidentTimesRef = useRef<HTMLDivElement>(null);
    const addressesRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const classificationRef = useRef<HTMLDivElement>(null);

    // Map error keys to their corresponding refs
    const errorRefs = {
      policeService: policeServiceRef,
      type: typeRef,
      associatedCrimes: associatedCrimesRef,
      operationalArea: operationalAreaRef,
      occurrenceNumbers: occurrenceNumbersRef,
      incidentTimes: incidentTimesRef,
      addresses: addressesRef,
      description: descriptionRef,
      classification: classificationRef,
    };

    useImperativeHandle(ref, () => ({
      scrollToFirstError: () => {
        // Find the first error
        const firstErrorKey = Object.keys(errors || {}).find((key) => {
          if (key === "contactName") {
            return Object.values(errors[key]).some((value) => value === true);
          }
          if (key === "addresses") {
            return errors[key].some(
              (address) => !address.isValid || !address.hasLocationType,
            );
          }
          return errors[key];
        });

        if (firstErrorKey && errorRefs[firstErrorKey]?.current) {
          errorRefs[firstErrorKey].current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      },
    }));

    useEffect(() => {
      setAlertProperty("rawAddresses", addresses);
    }, [addresses]);

    useEffect(() => {
      setAddresses(rawAddresses);
    }, [rawAddresses]);

    const errorMessages = {
      policeService: t("error_message_missing_police_service"),
      type: t("error_message_missing_type"),
      associatedCrimes: t("error_message_missing_associated_crimes"),
      operationalArea: t("error_message_missing_operational_area"),
      occurrenceNumbers: t("error_message_missing_occurrence_numbers"),
      description: t("error_message_missing_description"),
      // TODO: Add more error messages conditionally
      invalidAddress: t("error_message_invalid_address"),
      invalidLocationType: t("error_message_invalid_location_type"),
      incidentDate: t("error_message_invalid_incident_date"),

      classification: t("error_message_missing_classification"),
    };

    const helperTextStyling = {
      style: {
        color: colors.ERROR_TEXT_RED,
        marginLeft: "10px",
      },
    };

    useEffect(() => {
      if (props.validationAttempted) {
        generateValidationCriteria(alertProperties, policeServices).then(
          (res) => {
            setErrors(res);
          },
        );
      }
    }, [alertProperties]);

    const ClassificationDetails = ({
      classificationText,
    }: {
      classificationText: JSX.Element;
    }) => {
      return (
        <Typography
          variant="caption"
          style={{ color: "GrayText", lineHeight: "1" }}
        >
          {classificationText}{" "}
          <Link
            href="https://www.tbs-sct.canada.ca/pol/doc-eng.aspx?id=32614"
            target="_blank"
            rel="noopener noreferrer"
            color="secondary"
          >
            {t("read_more_here")}
          </Link>
        </Typography>
      );
    };

    return (
      <>
        <Paper elevation={4} className="alert-details-container">
          <div className="controls-row">
            <div className="control-parent" ref={policeServiceRef}>
              <FormControl fullWidth className="formControl">
                <Autocomplete
                  id="police-service-select-autocomplete"
                  options={policeServices as any}
                  getOptionLabel={(option: IPoliceService) =>
                    option?.name ? option.name : ""
                  }
                  value={policeService}
                  autoComplete
                  autoHighlight
                  clearOnBlur
                  filterSelectedOptions
                  fullWidth
                  openOnFocus
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  style={{
                    minWidth: "200px",
                  }}
                  filterOptions={filterPoliceServiceOptions as any}
                  size="small"
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      props.onPoliceServiceSelect(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors?.policeService}
                      required
                      label={t("issuing_police_service")}
                      variant="outlined"
                      margin="none"
                      helperText={
                        errors?.policeService ? errorMessages.policeService : ""
                      }
                      FormHelperTextProps={helperTextStyling}
                    />
                  )}
                  renderOption={(
                    { key, ...optionProps },
                    option,
                    { inputValue },
                  ) => {
                    const matches = match(option.name, inputValue, {
                      insideWords: true,
                    });
                    const parts = parse(option.name, matches);

                    return (
                      <li key={key} {...optionProps}>
                        <div>
                          {parts.map((part, index) => (
                            <span
                              key={index}
                              style={{
                                fontWeight: part.highlight ? 700 : 400,
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </li>
                    );
                  }}
                />
              </FormControl>
            </div>

            <div className="control-parent" ref={typeRef}>
              <FormControl fullWidth className="formControl">
                <Autocomplete
                  id="select-bulletin-type"
                  options={bulletinTypes.map((bt) => ({
                    value: bt.reference_name,
                    label: bt.name,
                  }))}
                  value={
                    props.type
                      ? {
                          value: props.type,
                          label:
                            bulletinTypes.find(
                              (bt) => bt.reference_name === props.type,
                            )?.name ?? props.type,
                        }
                      : null
                  }
                  autoComplete
                  autoHighlight
                  filterSelectedOptions
                  clearOnBlur
                  fullWidth
                  openOnFocus
                  style={{
                    minWidth: "200px",
                  }}
                  size="small"
                  onChange={(event, newValue) => {
                    if (newValue?.value) {
                      props.setType(newValue.value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors?.type}
                      required
                      label={t("bulletin_type")}
                      variant="outlined"
                      margin="none"
                      helperText={errors?.type ? errorMessages.type : ""}
                      FormHelperTextProps={helperTextStyling}
                    />
                  )}
                  renderOption={(
                    { key, ...optionProps },
                    option,
                    { inputValue },
                  ) => {
                    const isMatch = inputValue === option.label;
                    return (
                      <li
                        key={key}
                        {...optionProps}
                        style={{ fontWeight: isMatch ? 700 : 400 }}
                      >
                        {option.label}
                      </li>
                    );
                  }}
                />
              </FormControl>
            </div>
          </div>

          <div className="controls-row">
            <div className="control-parent" ref={associatedCrimesRef}>
              <FormControl fullWidth className="formControl">
                <Autocomplete
                  multiple
                  id="select-crime-types"
                  options={crimeTypes}
                  groupBy={(option) =>
                    t(`bulletin/associatedCrime:${option.group}`)
                  }
                  getOptionLabel={(option) => option.name ?? option.crime}
                  value={props.associatedCrimes}
                  isOptionEqualToValue={(option, value) =>
                    option.crime === value.crime
                  }
                  filterSelectedOptions
                  autoComplete
                  autoHighlight
                  clearOnBlur
                  fullWidth
                  openOnFocus
                  disableCloseOnSelect
                  size="small"
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          label={option.name ?? option.crime}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  onChange={(event, newValue) => {
                    props.updateState("associatedCrimes", newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors?.associatedCrimes}
                      label={t("associated_crimes")}
                      variant="outlined"
                      placeholder={
                        props.associatedCrimes.length
                          ? `${props.associatedCrimes.length} ${t("selected")}`
                          : t("select_all_that_apply")
                      }
                      required
                      margin="none"
                      helperText={
                        errors?.associatedCrimes
                          ? errorMessages.associatedCrimes
                          : ""
                      }
                      FormHelperTextProps={helperTextStyling}
                    />
                  )}
                  renderOption={(
                    { key, ...optionProps },
                    option,
                    { inputValue },
                  ) => {
                    const label = option.name ?? option.crime;
                    const matches = match(label, inputValue, {
                      insideWords: true,
                    });
                    const parts = parse(label, matches);

                    return (
                      <li key={key} {...optionProps}>
                        <div>
                          {parts.map((part, index) => (
                            <span
                              key={index}
                              style={{
                                fontWeight: part.highlight ? 700 : 400,
                                color: part.highlight ? "blue" : "black",
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </li>
                    );
                  }}
                />
              </FormControl>
            </div>
          </div>

          {operationalAreas && operationalAreas.length > 0 && (
            <div className="controls-row">
              <div className="control-parent" ref={operationalAreaRef}>
                <FormControl fullWidth className="formControl">
                  <Autocomplete
                    id="operational-area"
                    options={operationalAreas}
                    value={alertProperties.operationalArea}
                    autoComplete
                    autoHighlight
                    filterSelectedOptions
                    clearOnBlur
                    fullWidth
                    openOnFocus
                    style={{
                      minWidth: "200px",
                    }}
                    size="small"
                    onChange={(event, newValue) => {
                      props.updateState("operationalArea", newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={errors?.operationalArea}
                        required
                        label={t("operational_area")}
                        variant="outlined"
                        margin="none"
                        helperText={
                          errors?.operationalArea
                            ? errorMessages.operationalArea
                            : ""
                        }
                        FormHelperTextProps={helperTextStyling}
                      />
                    )}
                    renderOption={(
                      { key, ...optionProps },
                      option,
                      { inputValue },
                    ) => {
                      const matches = match(option, inputValue, {
                        insideWords: true,
                      });
                      const parts = parse(option, matches);

                      return (
                        <li key={key} {...optionProps}>
                          <div>
                            {parts.map((part, index) => (
                              <span
                                key={index}
                                style={{
                                  fontWeight: part.highlight ? 700 : 400,
                                }}
                              >
                                {part.text}
                              </span>
                            ))}
                          </div>
                        </li>
                      );
                    }}
                  />
                </FormControl>
              </div>
            </div>
          )}

          <div className="controls-row">
            <div className="control-parent" ref={occurrenceNumbersRef}>
              <FormControl fullWidth className="formControl">
                <Autocomplete
                  id="occurrence-number-fill"
                  options={[]}
                  autoHighlight
                  fullWidth
                  freeSolo
                  multiple
                  autoSelect
                  value={props.occurrenceNumbers}
                  size="small"
                  onChange={(event, newValue) => {
                    if (newValue[newValue.length - 1]?.inputValue) {
                      const newOccurenceNum = newValue[
                        newValue.length - 1
                      ].inputValue.replace(/\s+/g, "");
                      const duplicate = newValue
                        .slice(0, newValue.length - 1)
                        .find((value) => value === newOccurenceNum);
                      if (duplicate) {
                        toast.warning(t("duplicate_occurrence_number"));
                      } else {
                        props.updateState("occurrenceNumbers", [
                          ...newValue.slice(0, newValue.length - 1),
                          newOccurenceNum,
                        ]);
                      }
                    } else props.updateState("occurrenceNumbers", newValue);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          label={option}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  filterOptions={(options, params) => {
                    const filtered = filterOccuranceNumbers(options, params);
                    // Suggest the creation of a new value
                    if (params.inputValue !== "") {
                      filtered.push({
                        inputValue: params.inputValue,
                        label: `Press Enter or Click to add "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  getOptionLabel={(option) => {
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                      return option.label;
                    }
                    // Regular option
                    return option;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors?.occurrenceNumbers}
                      required
                      label={t("occurrence_number")}
                      variant="outlined"
                      margin="none"
                      helperText={
                        errors?.occurrenceNumbers
                          ? errorMessages.occurrenceNumbers
                          : ""
                      }
                      FormHelperTextProps={helperTextStyling}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>

          {/* Incident Time Selector */}
          <div ref={incidentTimesRef} className="controls-row">
            <IncidentTimeSelector
              incidentTimes={props.incidentTimes}
              setIncidentTimes={props.setIncidentTimes}
              errors={errors?.incidentTimes}
            />
          </div>

          <div className="controls-row">
            <div className="control-parent" ref={addressesRef}>
              <AddressList
                addresses={addresses}
                setAddresses={setAddresses}
                errors={errors?.addresses}
              />
            </div>
          </div>

          <div className="controls-row">
            <div className="control-parent" ref={descriptionRef}>
              <TextField
                id="date"
                label={t("description")}
                type="text"
                variant="outlined"
                fullWidth
                required
                multiline
                margin="none"
                minRows={4}
                className="text-input-mine"
                error={errors?.description}
                value={
                  alertProperties.originalLanguage === "fr"
                    ? (alertProperties.descriptionFr ?? "")
                    : (alertProperties.description ?? "")
                }
                onChange={(ev) => {
                  if (alertProperties.originalLanguage === "fr") {
                    props.updateState("descriptionFr", ev.target.value);
                  } else {
                    props.updateState("description", ev.target.value);
                  }
                }}
                FormHelperTextProps={helperTextStyling}
                helperText={
                  errors?.description ? errorMessages.description : ""
                }
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>
        </Paper>
        <Paper elevation={4} className="alert-details-container">
          <div ref={classificationRef}>
            <FormControl fullWidth className="formControl">
              <Autocomplete
                id="classification-select"
                options={Object.values(ALERT_CLASSIFICATION).map(
                  (classification) => ({
                    value: classification,
                    label: t(`bulletin/classification:${classification}`),
                  }),
                )}
                value={
                  alertProperties.classification
                    ? {
                        value: alertProperties.classification,
                        label: t(
                          `bulletin/classification:${alertProperties.classification}`,
                        ),
                      }
                    : null
                }
                autoComplete
                autoHighlight
                filterSelectedOptions
                clearOnBlur
                fullWidth
                openOnFocus
                size="small"
                onChange={(event, newValue) => {
                  setAlertProperty("classification", newValue?.value || null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("classification")}
                    variant="outlined"
                    margin="none"
                    required
                    FormHelperTextProps={helperTextStyling}
                    helperText={
                      errors?.classification ? errorMessages.classification : ""
                    }
                    error={errors?.classification}
                  />
                )}
              />
              {alertProperties.classification && (
                <ClassificationDetails
                  classificationText={
                    <div>
                      <Trans
                        t={t}
                        i18nKey={`bulletin/classification:${alertProperties.classification}_description`}
                        components={{ b: <b /> }}
                      />
                    </div>
                  }
                />
              )}
            </FormControl>
          </div>

          <ContactInfoBox
            contacts={contacts}
            setContacts={(contacts: any) => {
              setAlertProperty("contacts", contacts);
            }}
            policeService={policeService}
          />
          <div className="approved-by-container">
            <Typography>{t("approved_by")}:</Typography>
            <div className="controls-row">
              <div className="control-parent">
                <div className="controls-row-related">
                  <div className="control-parent1 ">
                    <TextField
                      label={t("name")}
                      type="text"
                      variant="outlined"
                      fullWidth
                      size="small"
                      className="text-input-mine"
                      name="approved_by"
                      value={alertProperties.approvedBy ?? ""}
                      placeholder="e.g. Cst. John Smith"
                      onChange={(ev) => {
                        props.updateState("approvedBy", ev.target.value);
                      }}
                    />
                  </div>
                  <div className="control-parent2 ">
                    <TextField
                      label="Badge #"
                      type="text"
                      variant="outlined"
                      fullWidth
                      size="small"
                      className="text-input-mine"
                      name="approved_by_badge"
                      value={alertProperties.approvedByBadge ?? ""}
                      onChange={(ev) => {
                        props.updateState("approvedByBadge", ev.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AdvancedDetails />
        </Paper>
      </>
    );
  },
);

DetailsStep.displayName = "DetailsStep";
